// ======================================================================
// 📁 File: src/modules/ppe/pages/planning/ProductionPlanningDashboard.jsx
// 📌 Module: PPE → Production Planning → Level 1 Dashboard
// ======================================================================

import React, { useEffect, useState } from "react";
import OrderItemPlanningTable from "./components/OrderItemPlanningTable";
import ProductionPlanningDrawer from "./ProductionPlanningDrawer";
import axiosClient from "../../../../api/axiosClient";

// ----------------------------------------------------------------------
// KPI CARD (same style as Routing / Process Master)
// ----------------------------------------------------------------------
const KpiCard = ({ title, value, color }) => (
  <div
    style={{
      flex: 1,
      background: "#fff",
      borderRadius: 12,
      padding: "12px 16px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
      borderLeft: `6px solid ${color}`,
    }}
  >
    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
      {title}
    </div>
    <div style={{ fontSize: 22, fontWeight: 600 }}>
      {value}
    </div>
  </div>
);

// ----------------------------------------------------------------------
// MAIN COMPONENT
// ----------------------------------------------------------------------
export default function ProductionPlanningDashboard() {
  // ------------------------------------------------------------------
  // FILTER STATE
  // ------------------------------------------------------------------
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("ALL");
  const [status, setStatus] = useState("ALL");

  // ------------------------------------------------------------------
  // PLANNING STATE
  // ------------------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrderItem, setSelectedOrderItem] = useState(null);

  // ------------------------------------------------------------------
  // DATA STATE (BACKEND)
  // ------------------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------------
  // LOAD PLANNING ORDER ITEMS (Phase-4 Entry API)
  // ------------------------------------------------------------------
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    axiosClient
      .get("/api/ppe/planning/dashboard/order-items")
      .then((res) => {
        if (!active) return;

        const data = Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        const normalized = data.map((r) => ({
        id: r.order_item_id,

        // 🔑 REQUIRED BY DRAWER
        order_item_id: r.order_item_id,
        routing_id: r.routing_assignment_id,   // ✅ ADD THIS

        orderNo: r.order_no,
        itemCode: `ITEM-${r.order_item_id}`,

        product: r.product_variant || "—",
        qty: r.quantity,
        priority: r.priority,

        dueDate: r.required_delivery_date,

        planStatus: r.plan_status || "DRAFT",
        planItemId: r.plan_item_id,
        risk: r.risk === true,
      }));
        setRows(normalized);
      })
      .catch((err) => {
        console.error("❌ Planning dashboard load failed:", err);
        if (active) setError("Failed to load planning items");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------------
  // FILTERING
  // ------------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.orderNo?.toLowerCase().includes(q) ||
      r.itemCode?.toLowerCase().includes(q) ||
      r.product?.toLowerCase().includes(q);

    const matchesPriority =
      priority === "ALL" || r.priority === priority;

    const matchesStatus =
      status === "ALL" || r.planStatus === status;

    return matchesSearch && matchesPriority && matchesStatus;
  });

  // ------------------------------------------------------------------
  // KPI STATS
  // ------------------------------------------------------------------
  const stats = {
    total: rows.length,
    pending: rows.filter((r) => !r.planItemId).length,
    planned: rows.filter((r) => r.planStatus === "PLANNED").length,
    risk: rows.filter((r) => r.risk).length,
  };

  // ------------------------------------------------------------------
  // PLAN HANDLER
  // ------------------------------------------------------------------
  const handlePlan = (row) => {
    console.log("✅ PLAN RECEIVED IN DASHBOARD:", row);
    setSelectedOrderItem(row);
    setDrawerOpen(true);
  };
  useEffect(() => {
  console.log("📌 drawerOpen =", drawerOpen);
  console.log("📌 selectedOrderItem =", selectedOrderItem);
}, [drawerOpen, selectedOrderItem]);

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div>
      {/* ================= HEADER ================= */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
        }}
      >
        <h4 style={{ margin: 0 }}>Production Planning</h4>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          Plan and monitor production at order item level
        </div>
      </div>

      {/* ================= KPI BAR ================= */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
        <KpiCard title="Total Order Items" value={stats.total} color="#0f766e" />
        <KpiCard title="Pending Planning" value={stats.pending} color="#f59e0b" />
        <KpiCard title="Planned" value={stats.planned} color="#16a34a" />
        <KpiCard title="At Risk" value={stats.risk} color="#dc2626" />
      </div>

      {/* ================= FILTERS ================= */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <input
          className="form-control"
          placeholder="Search Order / Item / Product"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="ALL">All Priorities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="NORMAL">Normal</option>
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="PLANNED">Planned</option>
        </select>
      </div>

      {/* ================= TABLE ================= */}
      {loading && <div className="text-muted">Loading planning items…</div>}
      {error && <div className="text-danger">{error}</div>}

      {!loading && !error && (
        <OrderItemPlanningTable
          data={filteredRows}
          onPlan={handlePlan}
        />
      )}
      
      {/* ================= DRAWER ================= */}
      <ProductionPlanningDrawer
        key={selectedOrderItem?.order_item_id}
        open={drawerOpen}
        orderItem={selectedOrderItem}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
